import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Button + small form that downloads a monthly PDF report.
 *
 * Fields exposed to the user:
 *   - Empresa (free-text, per design spec — appears on the cover)
 *   - Mês de referência (YYYY-MM picker)
 *
 * Calls GET /api/reports/monthly-client/{id}?month=YYYY-MM&company=<name>.
 */
function MonthlyReportButton({
  clientId,
  clientName = 'cliente',
  defaultCompany = '',
  size = 'sm',
  className = '',
}) {
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [company, setCompany] = useState(defaultCompany);
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!clientId) {
      toast.error('ID do cliente não disponível');
      return;
    }
    if (!month) {
      toast.error('Selecione o mês de referência');
      return;
    }
    setLoading(true);
    try {
      const params = { month };
      if (company && company.trim()) params.company = company.trim();
      const res = await api.get(`/reports/monthly-client/${clientId}`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = String(clientName).replace(/\s+/g, '_').replace(/[^\w\-_.]/g, '');
      a.download = `relatorio_${safeName}_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório gerado!');
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) toast.error('Cliente não encontrado');
      else if (status === 403) toast.error('Acesso negado para este cliente');
      else toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`inline-flex flex-wrap items-center gap-2 ${className}`}
      data-testid={`monthly-report-${clientId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        placeholder="Nome da empresa"
        className="h-9 px-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-md w-44 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid={`monthly-report-company-${clientId}`}
      />
      <input
        type="month"
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        className="h-9 px-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid={`monthly-report-month-${clientId}`}
      />
      <Button
        size={size}
        onClick={handleDownload}
        disabled={loading}
        className="bg-emerald-700 hover:bg-emerald-800 text-white"
        data-testid={`monthly-report-download-${clientId}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <Download className="w-4 h-4 mr-1" />
        )}
        Relatório PDF
      </Button>
    </div>
  );
}

export default MonthlyReportButton;
